import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Settings } from 'lucide-react';
import { Label } from '../UI/label';
import { userIsAdmin } from '../../Store/Reducers';

/**
 * EmailSharingComponent - Reusable component for email sharing functionality
 * 
 * @param {Object} props
 * @param {string} props.connID - Connection ID to check if customer has email
 * @param {boolean} props.isEmailShared - Current state of email sharing checkbox
 * @param {function} props.onEmailShareChange - Callback when checkbox state changes
 * @param {boolean} props.showInAddMode - Whether to show only in add mode (default: true)
 * @param {boolean} props.isEditMode - Whether current form is in edit mode (default: false)
 * @param {Array} props.connectionData - Array of connection data to find customer email
 * @param {boolean} props.isAdmin - Whether current user is admin
 * @param {function} props.onSettingsClick - Callback when settings icon is clicked
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.checkboxClassName - Additional CSS classes for checkbox
 * @param {string} props.labelClassName - Additional CSS classes for label
 * @param {Object} props.customStyles - Custom styling object
 * @param {boolean} props.showFeedbackOption - Whether to show the feedback checkbox (default: false)
 * @param {boolean} props.isFeedbackEnabled - Current state of feedback checkbox
 * @param {function} props.onFeedbackChange - Callback when feedback checkbox state changes
 * @param {boolean} props.showWhatsAppOption - Whether to show the WhatsApp checkbox (default: false)
 * @param {boolean} props.isWhatsAppShared - Current state of WhatsApp sharing checkbox
 * @param {function} props.onWhatsAppShareChange - Callback when WhatsApp checkbox state changes
 */
const SharingOptions = ({
    connID,
    isEmailShared = false,
    onEmailShareChange = () => { },
    showInAddMode = true,
    isEditMode = false,
    onSettingsClick,
    className = "",
    checkboxClassName = "",
    labelClassName = "",
    customStyles = {},
    showFeedbackOption = false,
    isFeedbackEnabled = false,
    onFeedbackChange = () => { },
    showWhatsAppOption = false,
    isWhatsAppShared = false,
    onWhatsAppShareChange = () => { },
    showCompletedSaleOption = false,
    isCompletedSale = false,
    onCompletedSaleChange = () => { }
}) => {
    const [hasCustomerEmail, setHasCustomerEmail] = useState(false);
    const [hasCustomerWhatsApp, setHasCustomerWhatsApp] = useState(false);

    // Get ownEmail from redux store
    const isAdmin = useSelector(userIsAdmin);
    const { ownEmail, googleReviewLink } = useSelector(state => state.masterData);
    const connectionData = useSelector(state => state.connectionData?.data || []);

    // Check if own email is configured
    const hasOwnEmail = ownEmail && ownEmail.length > 0 && ownEmail[0]?.length > 0;
    const hasGoogleRevLink = googleReviewLink && googleReviewLink.length > 0 && googleReviewLink[0]?.length > 0;

    // Check if customer has email and WhatsApp when connID changes
    useEffect(() => {
        if (!connID || !connectionData.length) {
            setHasCustomerEmail(false);
            setHasCustomerWhatsApp(false);
            return;
        }

        const connection = connectionData.find(conn => conn.ConnID == connID);

        // Check customer email
        const customerEmail = connection?.Email;
        const hasEmail = customerEmail && customerEmail.trim().length > 0;
        setHasCustomerEmail(hasEmail);

        // Check customer WhatsApp
        const customerWhatsApp = connection?.Mobile || connection?.Mobile;
        const hasWhatsApp = customerWhatsApp && customerWhatsApp.trim().length > 0;
        setHasCustomerWhatsApp(hasWhatsApp);

        onEmailShareChange(false);
        onWhatsAppShareChange(false);
    }, [connID]);

    // Handle email checkbox change
    const handleEmailCheckboxChange = (e) => {
        const isChecked = e.target.checked;

        if (!hasOwnEmail) {
            // No email configured → trigger settings
            if (onSettingsClick) {
                onSettingsClick("EMAIL");
            }
            return;
        }

        // If email sharing is being disabled, also disable feedback
        if (!isChecked && isFeedbackEnabled) {
            onFeedbackChange(false);
        }

        // Call parent callback with new state
        if (onEmailShareChange) {
            onEmailShareChange(isChecked);
        }
    };

    // Handle WhatsApp checkbox change
    const handleWhatsAppCheckboxChange = (e) => {
        const isChecked = e.target.checked;

        // Call parent callback with new state
        if (onWhatsAppShareChange) {
            onWhatsAppShareChange(isChecked);
        }
    };

    // Handle feedback checkbox change
    const handleFeedbackChange = (e) => {
        if (onFeedbackChange) {
            onFeedbackChange(e.target.checked);
        }
    };

    // Handle settings icon click
    const handleSettingsClick = (dialogType) => {
        if (onSettingsClick) {
            onSettingsClick(dialogType);
        }
    };

    // Don't render if conditions aren't met
    const shouldRenderEmail = hasCustomerEmail;
    const shouldRenderWhatsApp = showWhatsAppOption && hasCustomerWhatsApp;

    if (!shouldRenderEmail && !shouldRenderWhatsApp) return null;
    if (showInAddMode && isEditMode) return null;

    // Default styles
    const defaultStyles = {
        container: "space-y-3",
        checkboxContainer: "flex items-center space-x-2",
        checkbox: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500",
        label: "text-sm font-medium",
        settingsButton: "rounded-full hover:bg-gray-100 transition-colors duration-200 p-1",
        settingsIcon: "text-sahi-primary-color hover:text-gray-700"
    };

    // Merge custom styles with defaults
    const styles = { ...defaultStyles, ...customStyles };

    // Check if feedback should be enabled (email is shared and has own email)
    const isFeedbackCheckboxEnabled = hasOwnEmail && isEmailShared;

    return (
        <div className={`${styles.container} ${className}`}>
            {/* Responsive layout - stack on mobile, side by side on larger screens */}
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6">
                {/* Email Sharing Checkbox */}
                {shouldRenderEmail && (
                    <div className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            id={`shareByEmail-${connID}`}
                            checked={hasOwnEmail ? isEmailShared : false}
                            disabled={!hasOwnEmail}
                            onChange={handleEmailCheckboxChange}
                            className={`${styles.checkbox} ${checkboxClassName} ${hasOwnEmail ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                }`}
                        />
                        <Label
                            htmlFor={`shareByEmail-${connID}`}
                            className={`${styles.label} ${labelClassName} ${hasOwnEmail ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'
                                }`}
                            title={!isAdmin ? 'Ask admin to configure email' : ''}
                        >
                            Share by Email
                        </Label>

                        {/* Settings icon - show when no own email and user is admin */}
                        {!hasOwnEmail && isAdmin && (
                            <button
                                type="button"
                                onClick={() => handleSettingsClick("EMAIL")}
                                className={styles.settingsButton}
                                title="Configure your own email"
                            >
                                <Settings size={16} className={styles.settingsIcon} />
                            </button>
                        )}
                    </div>
                )}

                {/* Feedback Checkbox - Only show if showFeedbackOption is true */}
                {showFeedbackOption && hasOwnEmail && shouldRenderEmail && (
                    <div className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            id={`takeFeedback-${connID}`}
                            checked={isFeedbackCheckboxEnabled ? isFeedbackEnabled : false}
                            disabled={!isFeedbackCheckboxEnabled}
                            onChange={handleFeedbackChange}
                            className={`${styles.checkbox} ${checkboxClassName} ${isFeedbackCheckboxEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                }`}
                        />
                        <Label
                            htmlFor={`takeFeedback-${connID}`}
                            className={`${styles.label} ${labelClassName} ${isFeedbackCheckboxEnabled ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            Take Feedback
                        </Label>

                        {isFeedbackCheckboxEnabled && !hasGoogleRevLink && isAdmin && (
                            <button
                                type="button"
                                onClick={() => handleSettingsClick("GOOGLE_REVIEW_LINK")}
                                className={styles.settingsButton}
                                title="Configure your google reviews link"
                            >
                                <Settings size={16} className={styles.settingsIcon} />
                            </button>
                        )}
                    </div>
                )}

                {/* WhatsApp Sharing Checkbox - Only show if showWhatsAppOption is true */}
                {showWhatsAppOption && (
                    <div className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            id={`shareByWhatsApp-${connID}`}
                            checked={isWhatsAppShared}
                            onChange={handleWhatsAppCheckboxChange}
                            className={`${styles.checkbox} ${checkboxClassName} cursor-pointer`}
                        />
                        <Label
                            htmlFor={`shareByWhatsApp-${connID}`}
                            className={`${styles.label} ${labelClassName} text-gray-700 cursor-pointer`}
                            title={!isAdmin ? 'Ask admin to configure WhatsApp number' : ''}
                        >
                            Share by WhatsApp
                        </Label>
                    </div>
                )}

                {/* Completed Sale Checkbox - Only show if showCompletedSaleOption is true */}
                {showCompletedSaleOption && (
                    <div className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            id={`saveAsCompletedSale-${connID}`}
                            checked={isCompletedSale}
                            onChange={(e) => onCompletedSaleChange && onCompletedSaleChange(e.target.checked)}
                            className={`${styles.checkbox} ${checkboxClassName} cursor-pointer`}
                        />
                        <Label
                            htmlFor={`saveAsCompletedSale-${connID}`}
                            className={`${styles.label} ${labelClassName} text-gray-700 cursor-pointer`}
                        >
                            Save as Completed Sale
                        </Label>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharingOptions;